<template>
  <section style="">
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ title }}
          </p>
        </header>

        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <div class="columns">
              <div class="column">
                <form-group
                  name="gift_id"
                  attribute="attributes.gift"
                  :label="$t('message.select') + $t('attributes.gift')"
                  label-position="on-border"
                >
                  <b-autocomplete
                    ref="giftSelect"
                    v-model="search"
                    name="gift_id"
                    field="id"
                    :custom-formatter="t => t.full_name"
                    :data="gifts"
                    :loading="loadingGifts"
                    :placeholder="
                      $t('message.please_select') + $t('attributes.gift')
                    "
                    open-on-focus
                    @typing="onSearchGifts"
                    @select="onSelectGift"
                  >
                    <template #default="props">
                      <p>{{ props.option.full_name }}</p>
                    </template>
                    <template #empty>
                      {{
                        form.gift_id
                          ? loadingGifts
                            ? $t('attributes.searching')
                            : $t('attributes.not_found')
                          : $t('message.please_select') + $t('attributes.gift')
                      }}
                    </template>
                  </b-autocomplete>
                </form-group>

                <form-group
                  name="origin_quantity"
                  attribute="attributes.gift_origin_quantity"
                  :label="$t('attributes.gift_origin_quantity')"
                  label-position="on-border"
                >
                  <b-input
                    v-model.number="form.origin_quantity"
                    type="number"
                    name="origin_quantity"
                    disabled
                    expanded
                  >
                  </b-input>
                </form-group>

                <form-group
                  name="final_quantity"
                  attribute="attributes.gift_final_quantity"
                  :label="$t('attributes.gift_final_quantity')"
                  label-position="on-border"
                >
                  <b-input
                    v-model.number="form.final_quantity"
                    type="number"
                    name="final_quantity"
                    :placeholder="
                      $t('message.please_input') +
                        $t('attributes.gift_final_quantity')
                    "
                    expanded
                  >
                  </b-input>
                </form-group>

                <form-group
                  name="remark"
                  :label="$t('attributes.remark')"
                  label-position="on-border"
                >
                  <b-input
                    v-model="form.remark"
                    type="textarea"
                    name="amount"
                    maxlength="200"
                    expanded
                  >
                  </b-input>
                </form-group>
              </div>

              <div class="column">
                <form-group
                  name="date"
                  attribute="attributes.gift_transfer_date"
                  :label="$t('attributes.gift_transfer_date')"
                  label-position="on-border"
                >
                  <b-datepicker
                    v-model="transferDate"
                    :placeholder="
                      $t('message.please_input') +
                        $t('attributes.gift_transfer_date')
                    "
                    icon="calendar-today"
                    inline
                    trap-focus
                    @input="onSelectedDate"
                  >
                    <div class="buttons">
                      <b-button
                        :label="$t('attributes.today')"
                        type="is-success"
                        @click="onSelectedToday"
                      />
                      <b-button
                        :label="$t('attributes.yesterday')"
                        type="is-info"
                        @click="onSelectedYesterday"
                      />
                      <b-button
                        :label="$t('attributes.clear')"
                        type="is-danger"
                        @click="onClearDate"
                      />
                    </div>
                  </b-datepicker>
                </form-group>
              </div>
            </div>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button
            :loading="sending"
            class="is-fullwidth"
            @click="$parent.close()"
          >
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="sending"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{ $t('attributes.add') }}
          </b-button>
        </footer>

        <b-loading
          :active.sync="isLoading"
          :is-full-page="false"
          :can-cancel="false"
        ></b-loading>
      </div>
    </form>
  </section>
</template>

<script>
import { required, minValue } from 'vuelidate/lib/validators'
import { GIFTS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import debounce from 'lodash/debounce'

export default {
  name: 'GiftTransferImportForm',

  props: {
    sending: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      title: this.$t('attributes.gift_transfer_adjust'),

      search: null,
      loadingGifts: false,
      gifts: [],
      selectedGift: null,
      transferDate: null,

      form: {
        gift_id: null,
        date: null,
        origin_quantity: 0,
        final_quantity: null,
        remark: null
      }
    }
  },
  computed: {
    isLoading() {
      return this.loadingGifts || this.sending
    }
  },
  validations: {
    form: {
      gift_id: { required },
      date: { required },
      origin_quantity: { required },
      final_quantity: { required, minValue: minValue(0) },
      remark: { required }
    }
  },
  mounted() {
    this.$refs.giftSelect.focus()
    this.loadGiftsAsync(null)
  },
  methods: {
    async loadGiftsAsync(search) {
      try {
        const api = `${GIFTS_API}`
        const params = {
          search: search
        }

        this.loadingGifts = true
        const res = await this.axios.get(api, { params })
        this.gifts = res.data.data

        const gift = this.gifts.find(d => d.id === this.form.gift_id) || null

        if (gift) {
          this.$refs.giftSelect.setSelected(gift)
        }
        this.onSelectGift(gift)
      } catch (e) {
        this.gifts = []
        this.onSelectGift(null)

        notifyService.fetch_error('gift', e)
      } finally {
        this.loadingGifts = false
      }
    },
    onSearchGifts: debounce(function(search) {
      this.loadGiftsAsync(search)
    }, 500),
    onSelectGift(gift) {
      this.selectedGift = gift
      this.form.gift_id = gift && gift.id
      this.form.origin_quantity = gift ? gift.quantity : 0

      if (this.form.final_quantity === null) {
        this.form.final_quantity = gift && gift.quantity
      }
    },
    onSelectedToday() {
      this.transferDate = this.$dayjs().toDate()
      this.form.date = this.formatDate(this.transferDate)
    },
    onSelectedYesterday() {
      this.transferDate = this.$dayjs()
        .subtract(1, 'day')
        .toDate()
      this.form.date = this.formatDate(this.transferDate)
    },
    onClearDate() {
      this.transferDate = null
      this.form.date = null
    },
    onSelectedDate(date) {
      this.form.date = this.formatDate(date)
    },
    async onSend() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
